import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Clock, 
  Play,
  Pause,
  Lock,
  CheckCircle2,
  BookOpen,
  Video,
  User,
  ListVideo,
  MessageSquare,
  Star,
  Maximize,
  Volume2,
  VolumeX,
  RotateCcw,
  ExternalLink
} from 'lucide-react';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const WatchCourse = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const progressSaveTimer = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [content, setContent] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState(null);
  const [watchProgress, setWatchProgress] = useState(null);
  
  // Video player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    fetchCourseContent();
    
    return () => {
      // Clean up timer on unmount
      if (progressSaveTimer.current) {
        clearInterval(progressSaveTimer.current);
      }
    };
  }, [id]);

  const fetchCourseContent = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Lütfen giriş yapın');
        navigate('/login', { state: { from: `/watch/${id}` } });
        return;
      }

      // First get course details
      const courseResponse = await axios.get(`${API}/lessons/${id}`);
      setCourse(courseResponse.data);

      // Then try to get content (only works if purchased)
      try {
        const contentResponse = await axios.get(`${API}/lessons/${id}/content`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setContent(contentResponse.data);
        setHasAccess(true);
        
        // Set saved progress
        if (contentResponse.data.watch_progress) {
          setWatchProgress(contentResponse.data.watch_progress);
        }
      } catch (err) {
        if (err.response?.status === 403) {
          setHasAccess(false);
          setError('Bu kursu izlemek için satın almanız gerekiyor.');
        } else {
          throw err;
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 404) {
        toast.error('Kurs bulunamadı');
        navigate('/my-courses');
      } else {
        toast.error('Kurs yüklenirken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  // Save progress to backend
  const saveProgress = useCallback(async (time, dur) => {
    if (!hasAccess || dur === 0) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/user/watch-progress/${id}`,
        { current_time: time, duration: dur },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Also save to localStorage as fallback
      localStorage.setItem(`watch_progress_${id}`, JSON.stringify({
        current_time: time,
        duration: dur,
        timestamp: Date.now()
      }));
    } catch (err) {
      console.error('Failed to save progress:', err);
    }
  }, [id, hasAccess]);

  // Video event handlers
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const dur = videoRef.current.duration;
      setDuration(dur);
      
      // Resume from saved position
      let resumeTime = 0;
      
      if (watchProgress?.current_time) {
        resumeTime = watchProgress.current_time;
      } else {
        // Check localStorage fallback
        const saved = localStorage.getItem(`watch_progress_${id}`);
        if (saved) {
          const parsed = JSON.parse(saved);
          resumeTime = parsed.current_time;
        }
      }
      
      if (resumeTime > 0 && resumeTime < dur - 5) {
        videoRef.current.currentTime = resumeTime;
        toast.info(`Kaldığınız yerden devam ediliyor: ${formatTime(resumeTime)}`);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
    
    // Start periodic progress saving (every 10 seconds)
    if (progressSaveTimer.current) {
      clearInterval(progressSaveTimer.current);
    }
    progressSaveTimer.current = setInterval(() => {
      if (videoRef.current) {
        saveProgress(videoRef.current.currentTime, videoRef.current.duration);
      }
    }, 10000);
  };

  const handlePause = () => {
    setIsPlaying(false);
    
    // Save progress when pausing
    if (videoRef.current) {
      saveProgress(videoRef.current.currentTime, videoRef.current.duration);
    }
    
    // Clear timer
    if (progressSaveTimer.current) {
      clearInterval(progressSaveTimer.current);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    // Save 100% completion
    if (videoRef.current) {
      saveProgress(videoRef.current.duration, videoRef.current.duration);
    }
    toast.success('Kursu tamamladınız!');
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const handleSeek = (e) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const newTime = (clickX / rect.width) * duration;
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const restartVideo = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      pilates: 'Pilates',
      yoga: 'Yoga',
      meditation: 'Meditasyon',
      fitness: 'Fitness',
      breathing: 'Nefes',
      mobility: 'Mobilite'
    };
    return labels[category] || category;
  };

  const getLevelLabel = (level) => {
    const labels = {
      beginner: 'Başlangıç',
      intermediate: 'Orta',
      advanced: 'İleri'
    };
    return labels[level] || level;
  };

  // Check if video URL is embeddable (YouTube, Vimeo)
  const getVideoEmbed = (url) => {
    if (!url) return null;

    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (youtubeMatch) {
      return { type: 'youtube', src: `https://www.youtube.com/embed/${youtubeMatch[1]}` };
    }

    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return { type: 'vimeo', src: `https://player.vimeo.com/video/${vimeoMatch[1]}` };
    }

    // Direct video URL
    return { type: 'direct', src: url };
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (loading) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Access Denied - Redirect to Course Detail
  if (!hasAccess) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Button 
            variant="ghost" 
            className="mb-6"
            onClick={() => navigate('/lessons')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Kurslara Dön
          </Button>

          <Card className="text-center py-12 border-orange-200 bg-orange-50/50">
            <CardContent>
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="h-10 w-10 text-orange-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-gray-800">Bu Kursa Erişiminiz Yok</h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                {error || 'Bu kursun videolarını izlemek için önce satın almanız gerekiyor.'}
              </p>
              
              {course && (
                <div className="bg-white rounded-lg p-6 max-w-sm mx-auto mb-6 shadow-sm">
                  <h3 className="font-semibold text-lg mb-2">{course.title}</h3>
                  {course.instructor && (
                    <p className="text-sm text-gray-500 mb-3">
                      Eğitmen: {course.instructor.full_name}
                    </p>
                  )}
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Badge variant="secondary">{getCategoryLabel(course.category)}</Badge>
                    <Badge variant="outline">{getLevelLabel(course.level)}</Badge>
                  </div>
                  <p className="text-3xl font-bold text-purple-600">₺{course.price}</p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  className="btn-primary"
                  onClick={() => navigate(`/lessons/${id}`)}
                >
                  Kursu Satın Al
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/lessons')}
                >
                  Diğer Kursları Keşfet
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Get the best available video URL
  const getActiveVideoUrl = () => {
    if (!content) return null;
    
    // Priority: live_stream_url > recording_url > video_url
    if (content.live_stream_url) return content.live_stream_url;
    if (content.recording_url) return content.recording_url;
    if (content.video_url) return content.video_url;
    return null;
  };

  const activeVideoUrl = getActiveVideoUrl();
  const videoEmbed = activeVideoUrl ? getVideoEmbed(activeVideoUrl) : null;

  // Check if it's a live lesson (Zoom/Meet link)
  const isZoomOrMeet = activeVideoUrl && (
    activeVideoUrl.includes('zoom.us') || 
    activeVideoUrl.includes('meet.google.com') ||
    activeVideoUrl.includes('teams.microsoft.com')
  );

  return (
    <div className="min-h-screen pt-16 bg-gray-900">
      {/* Main Content Area */}
      <div className="flex flex-col lg:flex-row">
        
        {/* Video Section - Left/Main */}
        <div className="flex-1 lg:max-w-[calc(100%-380px)]">
          {/* Back Button */}
          <div className="px-4 py-3">
            <Button 
              variant="ghost" 
              className="text-white hover:bg-white/10"
              onClick={() => navigate('/my-courses')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Kurslarıma Dön
            </Button>
          </div>

          {/* Video Player */}
          <div className="px-4 pb-4">
            {/* Zoom/Meet/Teams - External Link */}
            {isZoomOrMeet ? (
              <div className="relative bg-gradient-to-br from-purple-900 to-indigo-900 rounded-lg overflow-hidden aspect-video flex flex-col items-center justify-center text-white p-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Video className="h-10 w-10" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{content?.title || course?.title}</h3>
                  <p className="text-purple-200 mb-6">
                    {content?.lesson_type === 'live' 
                      ? 'Canlı derse katılmak için aşağıdaki butona tıklayın'
                      : 'Dersi izlemek için aşağıdaki butona tıklayın'}
                  </p>
                  <Button 
                    size="lg"
                    className="bg-white text-purple-900 hover:bg-gray-100"
                    onClick={() => window.open(activeVideoUrl, '_blank')}
                  >
                    <ExternalLink className="h-5 w-5 mr-2" />
                    {activeVideoUrl.includes('zoom') ? 'Zoom Toplantısına Katıl' :
                     activeVideoUrl.includes('meet.google') ? 'Google Meet\'e Katıl' :
                     activeVideoUrl.includes('teams') ? 'Teams Toplantısına Katıl' :
                     'Derse Katıl'}
                  </Button>
                  <p className="text-sm text-purple-300 mt-4">
                    Link: {activeVideoUrl.substring(0, 50)}...
                  </p>
                </div>
              </div>
            ) : videoEmbed ? (
              <div className="relative bg-black rounded-lg overflow-hidden">
                {videoEmbed.type === 'direct' ? (
                  <>
                    <video 
                      ref={videoRef}
                      className="w-full aspect-video"
                      poster={course?.thumbnail_url ? `${API}${course.thumbnail_url}` : undefined}
                      onLoadedMetadata={handleLoadedMetadata}
                      onTimeUpdate={handleTimeUpdate}
                      onPlay={handlePlay}
                      onPause={handlePause}
                      onEnded={handleEnded}
                      onClick={togglePlay}
                    >
                      <source src={videoEmbed.src.startsWith('/') ? `${API}${videoEmbed.src}` : videoEmbed.src} type="video/mp4" />
                      Tarayıcınız video oynatmayı desteklemiyor.
                    </video>
                    
                    {/* Custom Controls */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4">
                      {/* Progress Bar */}
                      <div 
                        className="w-full h-1 bg-white/30 rounded-full cursor-pointer mb-3 group"
                        onClick={handleSeek}
                      >
                        <div 
                          className="h-full bg-purple-500 rounded-full relative"
                          style={{ width: `${progressPercent}%` }}
                        >
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      
                      {/* Controls Row */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-white hover:bg-white/20 h-10 w-10"
                            onClick={togglePlay}
                          >
                            {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                          </Button>
                          
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-white hover:bg-white/20 h-10 w-10"
                            onClick={restartVideo}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                          
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-white hover:bg-white/20 h-10 w-10"
                            onClick={toggleMute}
                          >
                            {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                          </Button>
                          
                          <span className="text-white text-sm">
                            {formatTime(currentTime)} / {formatTime(duration)}
                          </span>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-white hover:bg-white/20 h-10 w-10"
                          onClick={toggleFullscreen}
                        >
                          <Maximize className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  /* YouTube/Vimeo Embed */
                  <iframe
                    src={videoEmbed.src}
                    className="w-full aspect-video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={course?.title}
                  />
                )}
              </div>
            ) : (
              <div className="aspect-video bg-gray-800 rounded-lg flex items-center justify-center">
                <div className="text-center text-white">
                  <Video className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Video henüz eklenmemiş</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Eğitmen yakında video ekleyecek
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Course Info - Below Video */}
          <div className="px-4 pb-8">
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6">
                {/* Title and Badges */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge className="bg-green-500 text-white">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Satın Alındı
                  </Badge>
                  <Badge variant="secondary" className="bg-purple-600/20 text-purple-300 border-purple-500/30">
                    {getCategoryLabel(course?.category)}
                  </Badge>
                  <Badge variant="outline" className="border-gray-600 text-gray-300">
                    {getLevelLabel(course?.level)}
                  </Badge>
                </div>
                
                <h1 className="text-2xl font-bold text-white mb-2">{course?.title}</h1>
                {course?.subtitle && (
                  <p className="text-gray-400 mb-4">{course.subtitle}</p>
                )}

                {/* Instructor Info */}
                {course?.instructor && (
                  <div className="flex items-center gap-3 mb-4 p-3 bg-gray-700/50 rounded-lg">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-purple-600 text-white text-lg">
                        {course.instructor.full_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-white font-medium">{course.instructor.full_name}</p>
                      <p className="text-gray-400 text-sm">Eğitmen</p>
                    </div>
                  </div>
                )}

                {/* Duration */}
                <div className="flex items-center text-gray-400 mb-4">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>{course?.duration} dakika</span>
                </div>

                <Separator className="bg-gray-700 my-4" />
                
                {/* Description */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Kurs Hakkında</h3>
                  <p className="text-gray-300 leading-relaxed">{course?.description}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Sidebar - Right (Course Content) */}
        <div className="lg:w-[380px] bg-gray-850 border-l border-gray-700 lg:min-h-[calc(100vh-64px)]">
          <div className="p-4 sticky top-16">
            {/* Your Progress Card */}
            <Card className="bg-gray-800 border-gray-700 mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center">
                  <BookOpen className="h-5 w-5 mr-2 text-purple-400" />
                  İlerlemeniz
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-2">
                  <Progress 
                    value={watchProgress?.progress_percent || progressPercent} 
                    className="h-2 bg-gray-700"
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">
                    {Math.round(watchProgress?.progress_percent || progressPercent)}% tamamlandı
                  </span>
                  {watchProgress?.completed && (
                    <Badge className="bg-green-500 text-xs">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Tamamlandı
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Course Content - Future Expansion Ready */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center">
                  <ListVideo className="h-5 w-5 mr-2 text-purple-400" />
                  Kurs İçeriği
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* For Phase 1, we only have a single video per course */}
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 bg-purple-600/20 rounded-lg border border-purple-500/30">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                      <Play className="h-4 w-4 text-white" fill="white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium text-sm">{course?.title}</p>
                      <p className="text-gray-400 text-xs">{course?.duration} dakika</p>
                    </div>
                    {(watchProgress?.completed || progressPercent >= 90) && (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    )}
                  </div>
                </div>

                {/* Placeholder for future chapters */}
                <div className="mt-4 p-3 border border-dashed border-gray-600 rounded-lg text-center">
                  <p className="text-gray-500 text-sm">
                    Yakında daha fazla içerik eklenecek
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Future Features Placeholders */}
            <div className="mt-4 space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start text-gray-400 border-gray-700 hover:bg-gray-800"
                disabled
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Soru & Cevap (Yakında)
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start text-gray-400 border-gray-700 hover:bg-gray-800"
                disabled
              >
                <Star className="h-4 w-4 mr-2" />
                Değerlendirme (Yakında)
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WatchCourse;
